import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { 
  FiArrowLeft,
  FiUsers,
  FiMessageSquare,
  FiZap,
  FiEye,
  FiRefreshCw,
  FiCheck,
  FiStar,
  FiTrendingUp,
  FiTarget,
  FiCpu,
  FiLayers
} from 'react-icons/fi';

const MultiAIGuide = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const steps = [
    {
      step: 1,
      title: 'Click on the "Single" Label',
      description: 'Transform your conversation into multi-AI mode by clicking the "Single" label.',
      image: null,
      details: [
        'Look for the "Single" label at the top of your conversation',
        'Click directly on the "Single" text',
        'This will open the AI model selection menu',
        'You can now choose multiple AI models for your conversation'
      ],
      highlight: true
    },
    {
      step: 2,
      title: 'Select Multiple AI Models',
      description: 'Choose which AI models you want to participate in your conversation.',
      image: '/screenshots/multi-ai-step2-select-models.png',
      details: [
        'Browse through available AI models (OpenAI, Claude, Gemini, etc.)',
        'Select multiple models by checking their boxes',
        'Each model will provide its unique perspective',
        'Confirm your selection to activate multi-AI mode'
      ],
      hasScreenshot: true
    },
    {
      step: 3,
      title: 'Ask Your Question',
      description: 'Type your question and watch multiple AI models respond simultaneously.',
      image: null,
      details: [
        'Type your question in the message box',
        'Press Enter or click Send',
        'All selected AI models will process your question',
        'You\'ll see responses from each model side by side'
      ]
    },
    {
      step: 4,
      title: 'Compare and Analyze Responses',
      description: 'Review different perspectives and insights from multiple AI models.',
      image: null,
      details: [
        'Each AI response will be clearly labeled',
        'Compare different approaches and solutions',
        'Look for consensus or disagreements between models',
        'Use the insights to make better decisions'
      ]
    }
  ];

  const benefits = [
    {
      icon: FiEye,
      title: 'Multiple Perspectives',
      description: 'Get diverse viewpoints on the same question from different AI models.'
    },
    {
      icon: FiTrendingUp,
      title: 'Better Decision Making',
      description: 'Make more informed decisions by comparing multiple AI responses.'
    },
    {
      icon: FiRefreshCw,
      title: 'Reduced Bias',
      description: 'Minimize single-model bias by consulting multiple AI systems.'
    },
    {
      icon: FiTarget,
      title: 'Comprehensive Solutions',
      description: 'Get more complete solutions by leveraging different AI strengths.'
    },
    {
      icon: FiZap,
      title: 'Enhanced Creativity',
      description: 'Spark creativity with varied approaches from different AI models.'
    },
    {
      icon: FiStar,
      title: 'Quality Assurance',
      description: 'Cross-verify information accuracy across multiple AI sources.'
    }
  ];

  const tips = [
    {
      title: 'Choose Complementary Models',
      description: 'Select AI models with different strengths for comprehensive coverage.'
    },
    {
      title: 'Start with 2-3 Models',
      description: 'Begin with a manageable number of models to avoid information overload.'
    },
    {
      title: 'Compare Response Quality',
      description: 'Look for which models provide the most accurate and helpful responses.'
    },
    {
      title: 'Use for Complex Problems',
      description: 'Multi-AI works best for complex questions that benefit from multiple perspectives.'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      <Header />
      
      <div className="pt-20">
        {/* Breadcrumb */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Link 
            to="/guide" 
            className="inline-flex items-center text-purple-400 hover:text-purple-300 transition-colors"
          >
            <FiArrowLeft className="w-4 h-4 mr-2" />
            Back to Guide
          </Link>
          <div className="text-gray-400 mt-2">
            <span>Guide</span> / <span className="text-purple-400">Multi-AI Conversations</span>
          </div>
        </div>

        {/* Hero Section */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-purple-500 to-cyan-500 rounded-2xl mb-8">
              <FiLayers className="w-10 h-10 text-white" />
            </div>
            
            <h1 className="text-4xl md:text-6xl font-display font-bold mb-6 gradient-text">
              Multi-AI Conversations
            </h1>
            
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
              Harness the power of multiple AI models simultaneously. Get diverse perspectives, 
              compare responses, and make better decisions with our multi-AI conversation feature.
            </p>

            <div className="flex items-center justify-center space-x-6 mt-8">
              <div className="flex items-center space-x-2 text-gray-400">
                <FiUsers className="w-5 h-5" />
                <span>Multiple AI Models</span>
              </div>
              <div className="flex items-center space-x-2 text-gray-400">
                <FiMessageSquare className="w-5 h-5" />
                <span>Real-time Responses</span>
              </div>
              <div className="flex items-center space-x-2 text-gray-400">
                <FiZap className="w-5 h-5" />
                <span>Instant Comparison</span>
              </div>
            </div>
          </motion.div>
        </section>

        {/* Step-by-Step Guide */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-display font-bold mb-6 gradient-text">
              How to Use Multi-AI Mode
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Follow these simple steps to enable and use multi-AI conversations effectively
            </p>
          </motion.div>

          <div className="space-y-16">
            {steps.map((step, index) => (
              <motion.div
                key={step.step}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className={`relative ${step.highlight ? 'bg-gradient-to-r from-purple-500/10 to-cyan-500/10 border border-purple-500/20 rounded-2xl p-8' : ''}`}
              >
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                  <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                    <div className="flex items-start space-x-4">
                      <div className={`flex-shrink-0 w-12 h-12 ${step.highlight ? 'bg-gradient-to-r from-purple-500 to-cyan-500' : 'bg-gray-700'} rounded-xl flex items-center justify-center text-white font-bold text-lg`}>
                        {step.step}
                      </div>
                      <div className="flex-1">
                        <h3 className={`text-2xl font-bold mb-4 ${step.highlight ? 'text-purple-300' : 'text-white'}`}>
                          {step.title}
                        </h3>
                        <p className="text-gray-300 text-lg mb-6">
                          {step.description}
                        </p>
                        <ul className="space-y-2">
                          {step.details.map((detail, detailIndex) => (
                            <li key={detailIndex} className="flex items-start space-x-3">
                              <FiCheck className={`w-5 h-5 ${step.highlight ? 'text-purple-400' : 'text-green-400'} flex-shrink-0 mt-0.5`} />
                              <span className="text-gray-300">{detail}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                  
                  <div className={`${index % 2 === 1 ? 'lg:order-1' : ''} ${step.highlight ? 'relative' : ''}`}>
                    {step.highlight && (
                      <div className="absolute -inset-2 bg-gradient-to-r from-purple-500 to-cyan-500 rounded-2xl opacity-20 animate-pulse"></div>
                    )}
                    <div className={`${step.highlight ? 'relative' : ''} bg-gray-800/50 rounded-xl p-8 border border-gray-700`}>
                      {step.step === 1 ? (
                        <div className="text-center">
                          <div className="bg-gray-700 rounded-lg p-4 mb-4">
                            <div className="flex items-center justify-center space-x-2 mb-2">
                              <FiCpu className="w-5 h-5 text-purple-400" />
                              <span className="text-white font-medium">Current Mode:</span>
                            </div>
                            <div className="inline-block bg-gradient-to-r from-purple-500 to-cyan-500 text-white px-4 py-2 rounded-lg font-semibold cursor-pointer hover:from-purple-600 hover:to-cyan-600 transition-all transform hover:scale-105">
                              Single ← Click Here!
                            </div>
                          </div>
                          <p className="text-purple-300 font-medium">
                            💡 Click on "Single" to switch to Multi-AI mode
                          </p>
                        </div>
                      ) : step.hasScreenshot && step.image ? (
                        <div className="text-center">
                          <img 
                            src={step.image} 
                            alt={`${step.title} - Step ${step.step}`}
                            className="w-full max-w-md mx-auto rounded-lg border border-gray-600 shadow-lg"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'block';
                            }}
                          />
                          <div className="text-center text-gray-400 hidden">
                            <FiMessageSquare className="w-16 h-16 mx-auto mb-4 opacity-50" />
                            <p>Screenshot for Step {step.step}</p>
                            <p className="text-sm mt-2">Image: {step.image}</p>
                          </div>
                        </div>
                      ) : (
                        <div className="text-center text-gray-400">
                          <FiMessageSquare className="w-16 h-16 mx-auto mb-4 opacity-50" />
                          <p className="text-lg font-medium mb-2">Step {step.step}: {step.title}</p>
                          <p className="text-sm">Follow the instructions on the left to complete this step</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Benefits Section */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-display font-bold mb-6 gradient-text">
              Why Use Multi-AI?
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Discover the advantages of consulting multiple AI models for your questions and tasks
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-gray-800/50 backdrop-blur border border-gray-700 rounded-xl p-6 hover:border-purple-500/50 transition-all duration-300"
              >
                <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-cyan-500 rounded-lg flex items-center justify-center mb-4">
                  <benefit.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{benefit.title}</h3>
                <p className="text-gray-300">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Tips Section */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-display font-bold mb-6 text-white">
              Pro Tips for Multi-AI Conversations
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {tips.map((tip, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-gradient-to-r from-purple-500/10 to-cyan-500/10 border border-purple-500/20 rounded-xl p-6"
              >
                <h3 className="text-lg font-semibold text-purple-300 mb-2">{tip.title}</h3>
                <p className="text-gray-300">{tip.description}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* CTA Section */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center bg-gradient-to-r from-purple-500/20 to-cyan-500/20 border border-purple-500/30 rounded-2xl p-12"
          >
            <h2 className="text-3xl md:text-4xl font-display font-bold mb-6 gradient-text">
              Ready to Try Multi-AI Conversations?
            </h2>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              Start experiencing the power of multiple AI perspectives today. Click on "Single" to unlock multi-AI mode!
            </p>
            <a
              href="https://bervin.appiworks.com/app"
              className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-purple-500 to-cyan-500 text-white font-semibold rounded-xl hover:from-purple-600 hover:to-cyan-600 transition-all duration-300 transform hover:scale-105"
            >
              <FiZap className="w-5 h-5 mr-2" />
              Try Multi-AI Now
            </a>
          </motion.div>
        </section>
      </div>

      <Footer />
    </div>
  );
};

export default MultiAIGuide;
